import React from "react";
import {
  Chip,
  Menu,
  MenuItem,
  ListSubheader,
  Divider,
  Tooltip,
  useTheme,
} from "@mui/material";

/** ura === 1 indica usuário ChatBot (bot de atendimento) */
const isChatBotUser = (user) => Number(user?.ura) === 1;

/**
 * Seletor unificado de atribuição:
 * - Fila (sem usuário / sem agente)
 * - Usuários (humanos)
 * - ChatBots (usuários com ura = 1)
 * - Agentes de IA
 *
 * value: { type: "queue" | "user" | "agent"; id: string|number|null; label: string; isChatBot?: boolean }
 */
const AssigneeSelector = ({
  value,
  onChange,
  users = [],
  agents = [],
  size = "small",
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const humanUsers = (users || []).filter((u) => !isChatBotUser(u));
  const chatbotUsers = (users || []).filter(isChatBotUser);

  const currentType = value?.type || "queue";
  const currentLabel = value?.label || "Fila";
  const currentIsChatBot = value?.isChatBot === true;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (next) => {
    onChange && onChange(next);
    handleClose();
  };

  const getAgentId = (agent) => {
    if (!agent || typeof agent !== "object") return null;
    if (agent.id != null) return agent.id;
    if (agent.id_agente != null) return agent.id_agente;
    if (agent.agentId != null) return agent.agentId;

    // Fallback: pega o primeiro campo numérico cujo nome contenha "id"
    const dynamicKey = Object.keys(agent).find((key) => {
      const lower = key.toLowerCase();
      return (
        lower.includes("id") &&
        agent[key] !== null &&
        agent[key] !== undefined &&
        (typeof agent[key] === "number" || !Number.isNaN(Number(agent[key])))
      );
    });

    return dynamicKey ? agent[dynamicKey] : null;
  };

  const getChipStyles = () => {
    if (currentType === "agent") {
      return {
        bg: theme.palette.secondary.main,
        color: theme.palette.common.white,
      };
    }
    if (currentType === "user") {
      if (currentIsChatBot) {
        return {
          bg: theme.palette.warning?.main || "#ed6c02",
          color: theme.palette.common.white,
        };
      }
      // Diferenciação visual:
      // - "Minha Solicitação" -> destaque em verde (sucesso)
      // - Outro usuário        -> tom azul padrão
      const isMine =
        typeof currentLabel === "string" &&
        currentLabel.toLowerCase().includes("minha solicitação");

      if (isMine) {
        return {
          bg: theme.palette.success.main,
          color: theme.palette.common.white,
        };
      }

      return {
        bg: theme.palette.info.main,
        color: theme.palette.common.white,
      };
    }

    const isNoUser =
      typeof currentLabel === "string" &&
      currentLabel.toLowerCase().includes("sem usuário");

    if (isNoUser) {
      const bg = theme.palette.error.light || "#ef9a9a";
      return {
        bg,
        color: theme.palette.getContrastText
          ? theme.palette.getContrastText(bg)
          : theme.palette.text.primary,
      };
    }

    return {
      bg: theme.palette.grey[800],
      color: theme.palette.common.white,
    };
  };

  const { bg, color } = getChipStyles();

  const displayLabel =
    currentType === "agent"
      ? currentLabel || "Agente de IA"
      : currentType === "user"
      ? currentIsChatBot
        ? (currentLabel && !String(currentLabel).endsWith("(ChatBot)")
          ? `${currentLabel} (ChatBot)`
          : currentLabel || "ChatBot")
        : currentLabel || "Usuário"
      : currentLabel || "Fila";

  return (
    <>
      <Tooltip title="Alterar responsável (fila / usuário / ChatBot / agente de IA)">
        <Chip
          label={displayLabel}
          size={size}
          onClick={handleOpen}
          sx={{
            backgroundColor: bg,
            color,
            fontSize: "0.75rem",
            height: size === "small" ? "24px" : "28px",
            cursor: "pointer",
            maxWidth: "220px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        />
      </Tooltip>

      <Menu
        id="assignee-selector-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{ "aria-labelledby": "assignee-chip-selector" }}
      >
        <MenuItem
          selected={currentType === "queue"}
          onClick={() =>
            handleSelect({
              type: "queue",
              id: null,
              label: "Fila",
            })
          }
        >
          Fila (sem atribuição)
        </MenuItem>

        {(agents?.length || 0) > 0 && (
          <>
            <Divider />
            <ListSubheader disableSticky>Agentes de IA</ListSubheader>
            {agents.map((agent) => {
              const aid = getAgentId(agent);
              return (
                <MenuItem
                  key={`agent-${aid ?? "sem-id"}`}
                  selected={
                    currentType === "agent" &&
                    String(value?.id ?? "") === String(aid ?? "")
                  }
                  onClick={() =>
                    handleSelect({
                      type: "agent",
                      id: aid,
                      label:
                        agent.name ??
                        agent.nome ??
                        `Agente #${aid ?? "desconhecido"}`,
                      raw: agent,
                    })
                  }
                  sx={{
                    fontWeight: "bold",
                    color: theme.palette.secondary.main,
                  }}
                >
                  {agent.name ?? agent.nome ?? `Agente #${aid ?? "sem-id"}`}
                </MenuItem>
              );
            })}
          </>
        )}

        {(humanUsers?.length || 0) > 0 && (
          <>
            <Divider />
            <ListSubheader disableSticky>Usuários</ListSubheader>
            {humanUsers.map((user) => (
              <MenuItem
                key={`user-${user.id}`}
                selected={
                  currentType === "user" &&
                  !value?.isChatBot &&
                  String(value?.id ?? "") === String(user.id ?? "")
                }
                onClick={() =>
                  handleSelect({
                    type: "user",
                    id: user.id,
                    label: user.nome ?? user.name ?? `Usuário #${user.id}`,
                    isChatBot: false,
                  })
                }
              >
                {user.nome ?? user.name ?? `Usuário #${user.id}`}
              </MenuItem>
            ))}
          </>
        )}

        {(chatbotUsers?.length || 0) > 0 && (
          <>
            <Divider />
            <ListSubheader disableSticky>ChatBots</ListSubheader>
            {chatbotUsers.map((user) => (
              <MenuItem
                key={`chatbot-${user.id}`}
                selected={
                  currentType === "user" &&
                  value?.isChatBot &&
                  String(value?.id ?? "") === String(user.id ?? "")
                }
                onClick={() =>
                  handleSelect({
                    type: "user",
                    id: user.id,
                    label: user.nome ?? user.name ?? `ChatBot #${user.id}`,
                    isChatBot: true,
                    raw: user,
                  })
                }
                sx={{
                  color: theme.palette.warning?.main || "#ed6c02",
                  fontWeight: 500,
                }}
              >
                {user.nome ?? user.name ?? `ChatBot #${user.id}`} (ChatBot)
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </>
  );
};

export default AssigneeSelector;

